/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : February 11, 2025
File            : App.js
Description     : 
    This file serves as the main entry point for routing all the pages in the project.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/

/* 
Dependencies:
- npm install react-scripts
- npm install react-calendar
- npm install react-router-dom
*/

// Import MUI
import { ThemeProvider } from '@mui/material/styles';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Theme from './theme/theme';

// Import Other Pages
import Index from './pages/index/Index';
import ManageServices from './pages/other-pages/ManageServices';
import Services from './pages/other-pages/Service';
import About from './pages/other-pages/About';
import Contact from './pages/other-pages/Contact';
import Settings from './pages/settings/Settings';

// Import NoPage or Error Page
import NoPage from './pages/no-page/NoPage';

// Import User Account System Pages
import SignIn from './pages/authentication/sign-in/SignIn';
import SignUp from './pages/authentication/sign-up/SignUp'; 
import ManagePersonnel from './pages/authentication/personnel-management/ManagePersonnel'; // READ, WRITE or UPDATE
import ForgotPassword from './pages/authentication/account-recovery/ForgotPassword'; // READ, WRITE or UPDATE

// Import Dashboards
import Dashboard from './pages/dashboard/Dashboard';

// Import Appointment System Pages
import PatientAppointmentBooking from './pages/appointment-system/PatientAppointmentBooking'; // READ OR WRITE
import PatientAppointmentStatus from './pages/appointment-system/PatientAppointmentStatus'; // READ OR WRITE
import ManageAppointment from './pages/appointment-system/ManageAppointment'; // READ, WRITE or UPDATE

// Import Patient Record System Pages
import PatientRecord from './pages/record-system/patient-record/PatientRecord'; // READ, WRITE or UPDATE
import PersonnelPatientRecord from './pages/record-system/personnnel-patient-record/PersonnelPatientRecord'; // READ, WRITE or UPDATE

// Import Inventory System Pages
import ManageInventory from './pages/inventory/ManageInventory'; // WRITE or UPDATE

// Import Accounting System Pages
import Analytics from './pages/accounting-system/Analytics'; // WRITE or UPDATE

// Added
import ProtectedRoute from './components/UI/organisms/routing/ProtectedRoute';
import Unauthorized from './pages/no-page/Unauthorized';
import VerifyEmail from './pages/authentication/verify/VerifyEmail';

import Test from "./pages/test"

function App() {
  return (
    <div>
      <ThemeProvider theme={Theme}>
        <BrowserRouter>
          <Routes>
            {/* Public Pages - No Authentication Required */}
            <Route path="/" element={
              <ProtectedRoute requireAuth={false}>
                <Index />
              </ProtectedRoute>
            } />
            <Route path="/test" element={
              <ProtectedRoute requireAuth={false}>
                <Test />
              </ProtectedRoute>
            } />
            <Route path="/about" element={
              <ProtectedRoute requireAuth={false}>
                <About />
              </ProtectedRoute>
            } />
            <Route path="/contact" element={
              <ProtectedRoute requireAuth={false}>
                <Contact />
              </ProtectedRoute>
            } />
            <Route path="/services" element={
              <ProtectedRoute requireAuth={false}>
                <Services />
              </ProtectedRoute>
            } />

            {/* User Account System Pages */}
            <Route path="/sign-in" element={
              <ProtectedRoute requireAuth={false}>
                <SignIn />
              </ProtectedRoute>
            } />

            {/* User Account System Pages */}
            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={['DentistOwner', 'AssociateDentist', 'ClinicStaff', 'Patient']}>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/sign-up" element={
              <ProtectedRoute requireAuth={false}>
                <SignUp />
              </ProtectedRoute>
            } />
            <Route path="/forgot-password" element={
              <ProtectedRoute requireAuth={false}>
                <ForgotPassword />
              </ProtectedRoute>
            } />
            <Route path="/verify-email" element={
              <ProtectedRoute requireAuth={false}>
                <VerifyEmail />
              </ProtectedRoute>
            } />
            <Route path="/patient-appointment-booking" element={
              <ProtectedRoute allowedRoles={['Patient']}>
                <PatientAppointmentBooking />
              </ProtectedRoute>
            } />
            <Route path="/patient-appointment-status" element={
              <ProtectedRoute allowedRoles={['Patient']}>
                <PatientAppointmentStatus />
              </ProtectedRoute>
            } />
            <Route path="/treatment-history" element={
              <ProtectedRoute allowedRoles={['Patient']}>
                <PatientRecord />
              </ProtectedRoute>
            } />
            <Route path="/manage-personnel" element={
              <ProtectedRoute allowedRoles={['DentistOwner']}>
                <ManagePersonnel />
              </ProtectedRoute>
            } />
            <Route path="/manage-appointment" element={
              <ProtectedRoute allowedRoles={['DentistOwner', 'AssociateDentist', 'ClinicStaff']}>
                <ManageAppointment />
              </ProtectedRoute>
            } />
            <Route path="/patient-record" element={
              <ProtectedRoute allowedRoles={['DentistOwner', 'AssociateDentist', 'ClinicStaff']}>
                <PersonnelPatientRecord />
              </ProtectedRoute>
            } />
            <Route path="/inventory" element={
              <ProtectedRoute allowedRoles={['DentistOwner', 'AssociateDentist', 'ClinicStaff']}>
                <ManageInventory />
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute allowedRoles={['DentistOwner']}>
                <Analytics />
              </ProtectedRoute>
            } />
            <Route path="/manage-services" element={
              <ProtectedRoute allowedRoles={['DentistOwner', 'AssociateDentist']}>
                <ManageServices />
              </ProtectedRoute>
            } />

            <Route path="/dashboard" element={
              <ProtectedRoute allowedRoles={['Patient', 'DentistOwner', 'AssociateDentist', 'ClinicStaff']}>
                <Dashboard />
              </ProtectedRoute>
            } />

            {/* Error Pages */}
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<NoPage />} />

          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;